import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axiosConfig';

const PERIOD_LABEL = { DAY: '매일', WEEK: '주', MONTH: '월' };

const STATUS_STYLE = {
  ACTIVE: { text: '진행', cls: 'bg-success-subtle text-success' },
  PAUSED: { text: '중지', cls: 'bg-warning-subtle text-warning-emphasis' },
  ENDED: { text: '종료', cls: 'bg-secondary-subtle text-secondary' },
};

const WINDOW_LABEL = { MORNING: '오전', AFTERNOON: '오후', NIGHT: '야간', ANYTIME: '시간 무관' };

const windowText = (windows) => {
  const list = Array.isArray(windows) ? windows : [];
  if (list.length === 0) return '시간 무관';
  return list
    .map((w) => {
      // 계획이 이름을 지어 두었으면 그것으로 부른다. 시간 무관 셋을 늘어놓아
      // 봐야 목록에서는 구분이 안 된다.
      const name = w.name || WINDOW_LABEL[w.label] || w.label;
      // 회사가 시각을 직접 정했으면 그대로 보여준다. 오전이 08:30~09:30 인지
      // 09:30~10:30 인지는 이름만으로 알 수 없다.
      const range = w.start && w.end ? ` ${w.start}~${w.end}` : '';
      const count = w.count > 1 ? ` ${w.count}회` : '';
      return `${name}${range}${count}`;
    })
    .join(' · ');
};

/**
 * 한 주에 몇 회인가.
 *
 * 경로를 고르는 방식에 따라 답이 달라진다. 경로마다 각각이면 경로 수만큼
 * 곱해지고, 합쳐서나 번갈아면 그대로다. 여기가 가장 오해가 잦은 지점이라
 * 목록에서 미리 풀어 보여준다.
 *
 * 경로 안의 들를 곳 수는 곱해지지 않는다. 다섯 곳을 들르든 한 곳을 들르든
 * 한 회차는 한 회차다.
 */
const weeklyCount = (plan) => {
  const routes = plan.routes?.length || 1;
  const perPeriod = plan.poolMode === 'PER_ROUTE' ? plan.quota * routes : plan.quota;
  if (plan.period === 'DAY') {
    // 매일이라고 해서 이레가 아니다. 경비 순찰은 하루 3회지만 월~금만 돈다.
    // 요일 규칙을 안 보면 21회로 나와 실제보다 많게 읽힌다.
    const days = plan.dayRule?.days;
    const workdays = Array.isArray(days) && days.length > 0 ? days.length : 7;
    return perPeriod * workdays;
  }
  if (plan.period === 'MONTH') return null; // 주 단위로 환산하면 오해를 부른다
  return perPeriod;
};

const InspectionPlanListPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPlans = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/inspection-plans');
      setPlans(res.data);
    } catch (err) {
      console.error('Failed to load inspection plans', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const active = plans.filter((p) => p.status === 'ACTIVE');
  // 경비 순찰처럼 매일 도는 것은 순회와 성격이 다르다. 일하는 사람이 달라서
  // 한 숫자로 더하면 아무 뜻이 없다.
  const rounds = active.filter((p) => p.period !== 'DAY');
  const roundsTotal = rounds.reduce((sum, p) => sum + (weeklyCount(p) || 0), 0);

  /**
   * 어디를 도는가.
   *
   * 경로가 여럿이면 경로 이름을 낸다 -- 그때는 '어느 경로를 도느냐' 가 계획의
   * 핵심이기 때문이다. 하나뿐이면 그 경로가 들르는 곳을 낸다.
   */
  const placeText = (plan) => {
    const routes = plan.routes || [];
    if (routes.length === 0) return '순회 경로 없음';

    if (routes.length > 1) {
      const names = routes.map((r, i) => r.name || `경로 ${i + 1}`).join(', ');
      return `순회 경로 ${routes.length}개 · ${names}`;
    }

    const stops = routes[0].stops || [];
    if (stops.length === 0) return '들를 곳 없음';
    const first = stops[0];
    const place = first.section?.name
      ? `${first.facility?.name} · ${first.section.name}`
      : `${first.facility?.name} 전체`;
    return stops.length > 1 ? `${place} 외 ${stops.length - 1}곳` : place;
  };

  return (
    <div className="pb-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="fw-bold mb-1">점검 계획</h4>
          <p className="text-muted small mb-0">언제 어느 순회 경로를 도는지 정합니다.</p>
        </div>
        <button
          className="btn btn-primary rounded-3"
          onClick={() => navigate('/inspection-plans/new')}
        >
          <i className="bi bi-plus-lg"></i>
        </button>
      </div>

      {loading ? (
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : plans.length === 0 ? (
        <div className="bg-white rounded-4 border text-center p-5">
          <i className="bi bi-calendar3 display-6 d-block mb-3 opacity-25"></i>
          <h6 className="fw-bold mb-1">아직 점검 계획이 없습니다</h6>
          <p className="text-muted small mb-0">
            계획을 만들면 그때부터 점검할 일이 날짜에 맞춰 생깁니다.
          </p>
        </div>
      ) : (
        <>
          {plans.map((plan) => {
            const status = STATUS_STYLE[plan.status] || STATUS_STYLE.ACTIVE;
            const perWeek = weeklyCount(plan);
            return (
              <div
                key={plan.id}
                className="bg-white rounded-4 border p-3 mb-2"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/inspection-plans/${plan.id}`)}
              >
                <div className="d-flex align-items-center gap-2 mb-1">
                  <span className="fw-bold">{plan.name}</span>
                  <span className={`badge rounded-pill ${status.cls}`} style={{ fontSize: '0.7rem' }}>
                    {status.text}
                  </span>
                  <i className="bi bi-chevron-right text-muted ms-auto small"></i>
                </div>

                <div className="text-muted small mb-1">{placeText(plan)}</div>

                {/* 누가 맡았는지 목록에서 보여야 한다. 열어봐야 아는 정보면
                    '아무도 안 맡은 계획' 을 알아채지 못한다. */}
                <div className="text-muted mb-1" style={{ fontSize: '0.72rem' }}>
                  <i className="bi bi-person me-1"></i>
                  {plan.assignedWorkers?.length > 0
                    ? plan.assignedWorkers.map((w) => w.name).join(', ')
                    : '누구나'}
                </div>

                <div className="d-flex flex-wrap gap-2 align-items-center">
                  <span className="badge bg-light text-dark border fw-normal">
                    {PERIOD_LABEL[plan.period]} {plan.quota}회
                  </span>
                  <span className="text-muted" style={{ fontSize: '0.78rem' }}>
                    {windowText(plan.windows)}
                  </span>
                  {perWeek != null && (
                    <span
                      className={`ms-auto ${plan.status === 'ACTIVE' ? 'text-primary' : 'text-muted'}`}
                      style={{ fontSize: '0.78rem' }}
                    >
                      {/* 중지된 계획은 돌지 않는다. 숫자만 적어 두면 도는 것처럼
                          읽히므로 '멈춤' 을 앞에 붙인다. */}
                      {plan.status === 'ACTIVE' ? `주 ${perWeek}회` : `멈춤 · 주 ${perWeek}회`}
                    </span>
                  )}
                </div>
              </div>
            );
          })}

          {/* 총량을 막지는 않고 보여만 준다. 사람이 감당할 수 있는 양인지는
              한 순회에 걸리는 시간과 근무 인원에 달렸고, 그건 시스템이 알 수
              없다. 막으면 우회하려고 가짜 데이터를 만들게 된다. */}
          {rounds.length > 0 && (
            <div className="bg-light rounded-3 p-3 mt-3">
              <div className="d-flex justify-content-between align-items-center">
                <span className="small text-muted">순회 계획 합계</span>
                <span className="fw-bold">주 {roundsTotal}회</span>
              </div>
              <div className="text-muted mt-1" style={{ fontSize: '0.72rem' }}>
                매일 도는 계획은 성격이 달라 합계에서 뺐습니다.
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default InspectionPlanListPage;
